"use client"

import { useBlockchain } from "@/app/blockchains/use-blockchain"
import { useDebouncedCallback } from "@/app/hooks/use-debounced-callback"
import { VerifiedIcon } from "@/app/icons/verified"
import { PriceWrapper } from "@/app/price"
import * as amplitude from "@amplitude/analytics-browser"
import { getBlockchainFromAddress } from "@anaconda/core/blockchain/address"
import { toCollectionId } from "@anaconda/core/nft-collection/utils"
import { toNftId } from "@anaconda/core/nft/utils"
import type { PriceValue } from "@anaconda/core/price/types"
import { useLingui } from "@lingui/react/macro"
import {
  Command,
  CommandInput,
  CommandItem,
  cn,
} from "@rarible_int/anaconda_ui_components"
import { reatomComponent } from "@reatom/react"
import { useMolecule } from "bunshi/react"
import Image from "next/image"
import { useRouter } from "next/navigation"
import { memo, useCallback, useEffect, useRef, useState } from "react"
import { SizedImage } from "../../components/image"
import { getCollectionHref } from "../../navigation/collection"
import { getItemHref } from "../../navigation/item"
import { CollectionSearchBlockchainMoleculeReatom } from "./collection-search-blockchain-molecule.reatom"
import { SearchPanelReatom } from "./search-panel.reatom"
import { useSearchReatom } from "./use-search.reatom"
import { useTrendingCollectionsReatom } from "./use-trending-collections.reatom"

const MAX_SEARCH_LENGTH = 100

function SearchItemSkeleton() {
  return (
    <div
      className="flex h-14 items-center gap-3 rounded bg-transparent px-3"
      data-state="Loading"
    >
      <div className="h-8 w-8 animate-pulse rounded-sm bg-foreground/20" />
      <div className="h-2.5 w-[122px] animate-pulse rounded bg-foreground/20" />
      <div className="ml-auto h-2.5 w-16 animate-pulse rounded bg-foreground/20" />
    </div>
  )
}

export const SearchItem = memo(function SearchItem({
  id,
  name,
  image,
  floorPrice,
  verified,
  onSelect,
  showBlockchain,
}: {
  id: string
  name: string
  image: string | null | undefined
  floorPrice?: PriceValue
  verified?: boolean
  onSelect: () => void
  showBlockchain: boolean
}) {
  const { t } = useLingui()
  const blockchain = getBlockchainFromAddress(id)
  const { icon } = useBlockchain(blockchain)

  return (
    <CommandItem
      value={`${name}_${id}`}
      keywords={[id]}
      onSelect={onSelect}
      className="flex h-14 cursor-pointer items-center gap-3 rounded px-3 hover:bg-accent"
      data-testid={`search-item-${id}`}
    >
      <div className="relative flex-none">
        <SizedImage
          imageSrc={image}
          alt={name}
          width={32}
          height={32}
          className="h-8 w-8 rounded-sm object-contain"
          data-testid={`search-item-image-${id}`}
        />
        {showBlockchain && (
          <Image
            unoptimized
            className="absolute right-0 bottom-0 z-20 translate-x-1/2 translate-y-1/2 transform"
            src={icon}
            width={12}
            height={12}
            alt={t`Blockchain icon for ${blockchain}`}
          />
        )}
      </div>
      <span className="flex items-center gap-1 text-foreground text-sm">
        {name}
        {verified && <VerifiedIcon className="flex-none" />}
      </span>

      <div className="ml-auto">
        {floorPrice ? (
          <PriceWrapper
            amount={floorPrice.amount}
            currency={floorPrice.currency}
            className="text-sm"
          />
        ) : (
          <span className="text-nowrap text-foreground/60 text-sm">—</span>
        )}
      </div>
    </CommandItem>
  )
})

export const SearchReatom = reatomComponent<{
  className?: string
  autoFocus?: boolean
}>(({ className, autoFocus }) => {
  const { t } = useLingui()
  const [value, setValue] = useState("")
  const router = useRouter()
  const searchRef = useRef<HTMLDivElement>(null)
  const {
    collectionResults,
    nftResults,
    isLoadingCollections,
    isLoadingNfts,
    isOpen,
    search,
    reset,
  } = useSearchReatom()
  const searchInputRef = useRef<HTMLInputElement>(null)
  const [showPanel, setShowPanel] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [isTyping, setIsTyping] = useState(false)
  const { data: trendingCollections, isLoading: isTrendingLoading } =
    useTrendingCollectionsReatom()

  const { selectedBlockchain, blockchainsMap } = useMolecule(
    CollectionSearchBlockchainMoleculeReatom,
  )

  const selectedBlockchainValue = selectedBlockchain()
  const blockchainsMapValue = blockchainsMap()

  const selectedBlockchainConfig =
    selectedBlockchainValue && selectedBlockchainValue !== "ALL"
      ? blockchainsMapValue[selectedBlockchainValue]
      : null
  const blockchainName = selectedBlockchainConfig?.name || null

  const placeholderText = blockchainName
    ? t`Search collections and items on ${blockchainName}`
    : t`Search collections and items`

  const handleClose = useCallback(() => {
    reset()
    setValue("")
    setShowPanel(false)
    setHasSearched(false)
    setIsTyping(false)
    searchInputRef.current?.blur()
  }, [reset])

  const handleCollectionClick = useCallback(
    (id: string) => {
      handleClose()
      router.push(getCollectionHref(toCollectionId(id)))
    },
    [handleClose, router],
  )

  const handleNftClick = useCallback(
    (id: string) => {
      handleClose()
      router.push(getItemHref(toNftId(id)))
    },
    [handleClose, router],
  )

  const debouncedSearch = useDebouncedCallback((query: string) => {
    setIsTyping(false)
    search(query)
    amplitude.track("Search", { query })
  }, 500)

  const handleEnter = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Enter" && value.trim()) {
        e.preventDefault()
        debouncedSearch(value.trim())
      }
    },
    [value, debouncedSearch],
  )

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleClose()

        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur()
        }
      }

      if (e.key === "Tab") {
        handleClose()
      }

      handleEnter(e)
    }

    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        handleClose()
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    document.addEventListener("mousedown", handleClickOutside)

    return () => {
      document.removeEventListener("keydown", handleKeyDown)
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [handleClose, handleEnter])

  useEffect(() => {
    if (autoFocus) {
      searchInputRef.current?.focus()
    }
  }, [autoFocus])

  const sanitizeQuery = (query: string) => {
    return query.slice(0, MAX_SEARCH_LENGTH)
  }

  const handleSearch = (query: string) => {
    const sanitizedQuery = sanitizeQuery(query)
    setValue(sanitizedQuery)

    if (sanitizedQuery.length > 0) {
      setHasSearched(true)
      setIsTyping(true)
      debouncedSearch(sanitizedQuery)
    } else {
      setHasSearched(false)
      setIsTyping(false)
      reset()
    }
  }

  const handleFocus = useCallback(() => {
    setShowPanel(true)
  }, [])

  // biome-ignore lint/correctness/useExhaustiveDependencies: Re-search when blockchain changes
  useEffect(() => {
    if (hasSearched && value.trim()) {
      setIsTyping(false)
      search(value.trim())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBlockchainValue])

  return (
    <div ref={searchRef}>
      <Command
        className={cn("relative overflow-visible", className)}
        data-testid="search"
        aria-expanded={isOpen}
        // biome-ignore lint/a11y/useSemanticElements: we can't use select here
        role="searchbox"
        aria-label={placeholderText}
        aria-controls="search-results"
        shouldFilter={false}
      >
        <CommandInput
          ref={searchInputRef}
          placeholder={placeholderText}
          onValueChange={handleSearch}
          onFocus={handleFocus}
          value={value}
          // 16px on mobile: iOS Safari zooms into inputs smaller than 16px on
          // focus. Not `text-base`: this project's Tailwind config makes it 14px.
          className="text-[16px] shadow-sm md:text-sm"
          data-testid="search-input"
          aria-label={placeholderText}
          name="search-input"
          maxLength={MAX_SEARCH_LENGTH}
        />
        {showPanel && (
          <div className="absolute top-full right-0 left-0 mt-1">
            <SearchPanelReatom
              hasSearched={hasSearched}
              collections={
                hasSearched ? collectionResults : trendingCollections
              }
              nfts={nftResults}
              isLoadingCollections={
                hasSearched ? isLoadingCollections || isTyping : false
              }
              isLoadingNfts={hasSearched ? isLoadingNfts || isTyping : false}
              isTrendingLoading={isTrendingLoading}
              onCollectionClick={handleCollectionClick}
              onNftClick={handleNftClick}
              SearchCollectionItemComponent={SearchItem}
              LoadingSkeletonComponent={SearchItemSkeleton}
            />
          </div>
        )}
      </Command>
    </div>
  )
}, "SearchReatom")
